/**
 * members.ts — Settings → Members (R4, 0138), the rules that need no React:
 * who may read the card (owner/admin, from /v1/me, fail closed), how a
 * member and their link read, and which employees the picker offers.
 * Nothing here counts anything per person.
 */
import type { MeResponse } from '../../types'
import type { MemberEmployeeOption, TenantMember } from '../../../../api/types'

export const MEMBERS_GATE_SENTENCE = 'Members and the employee each login acts as are visible to workspace owners and admins.'

/** The ExportsCard posture: owner or admin, decided from /v1/me; a session
 *  that has not resolved is refused (fail closed) and no request is made. */
export function canManageMembers(me: Pick<MeResponse, 'role'> | null | undefined): boolean {
  return me?.role === 'owner' || me?.role === 'admin'
}

/** The member's name, or a plain stand-in — never an email, never an id. */
export function memberName(m: Pick<TenantMember, 'displayName'>): string {
  return m.displayName && m.displayName.trim() !== '' ? m.displayName : 'Unnamed login'
}

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', developer: 'Developer', viewer: 'Viewer', billing: 'Billing' }
export function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role
}

const STATUS_LABEL: Record<string, string> = { active: 'Active', invited: 'Invited', revoked: 'Revoked' }
export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

/** What the "Acts as" cell says. A leaver's link stands and counts for
 *  nothing, and the sentence says exactly that. */
export function linkLine(m: Pick<TenantMember, 'employee'>): string {
  if (!m.employee) return 'Not linked'
  if (!m.employee.employed) return `${m.employee.employeeName} — has left, so this link acts on nothing`
  return m.employee.employeeName
}

/** A link is SET only on an active membership with no live link; a live link
 *  is CLEARED on any membership (a revoked member's link included). */
export function canSet(m: Pick<TenantMember, 'status' | 'employee'>): boolean {
  return m.status === 'active' && !m.employee
}
export function canClear(m: Pick<TenantMember, 'employee'>): boolean {
  return !!m.employee
}

/** The picker's choices: every employable person, the ones already linked to
 *  ANOTHER login disabled with the reason (ruling 1: one employee, one live
 *  login). */
export function pickerOptions(employees: readonly MemberEmployeeOption[], members: readonly TenantMember[]): { id: string; label: string; disabled: boolean }[] {
  const nameOf = new Map(members.map(m => [m.membershipId, memberName(m)] as const))
  return employees.map(e => e.linkedMembershipId
    ? { id: e.id, label: `${e.fullName} (linked to ${nameOf.get(e.linkedMembershipId) ?? 'another login'})`, disabled: true }
    : { id: e.id, label: e.fullName, disabled: false })
}
